import React, { FC } from "react";
import classnames from "classnames";
import { BadgeDefinition, BadgeLadderTier, FlameProgress as FlameProgressModel } from "@shared/models";
// "@assets/*" is a webpack-only alias (react-app-rewire-alias via config-overrides.js/paths.json) --
// jest has no moduleNameMapper for it, same as GoalHeader.tsx/BackBtn.tsx's own "@assets"-aliased SVGs.
// FlameProgress.test.tsx virtual-mocks this exact path, the same recipe sidebar.test.tsx uses for its
// one "@assets"-aliased SVG.
import flameCrystalEmblem from "@assets/images/spark/flame-crystal-emblem.png";

// Relative, not the "@containers" alias: jest resolves no path aliases in this repo, and this
// component's own suite needs the REAL CardHead rendering (asserting the heading text), matching
// ValueLadderCards' own relative import of the same component.
import { CardHead } from "../../CardHead";
import { flameEmblemArt } from "./flameEmblemArt";
import { getFlameLadderTiers } from "./flameLadderTiers";
import { flameSubtitle } from "./flameSubtitle";

import "./index.scss";

interface FlameProgressProps {
  flame: FlameProgressModel;
  // The badge catalog (`GET /badge-definitions`). The flame progress response only ever carries
  // `current_tier`/`next_tier` — two rungs of the ladder — never the full list or its length, so the
  // pip track's rung count and the right-hand "highest tier" threshold both come from here instead
  // (lifetime-flame-rebuild plan). `null` while it hasn't loaded yet or failed to load, independently
  // of `flame` itself.
  badgeDefinitions: BadgeDefinition[] | null;
}

type FlamePipState = "earned" | "current" | "locked";

/**
 * Classes each pip position 0..n-1 around the current tier's 0-based `index`, copied from
 * `ValueLadderCards`' own identical `buildPipStates` rather than extracted into a shared helper — this
 * epic's own established "copy the vertical slice, don't abstract a shared hook" convention
 * (RedemptionsContainer/index.scss's precedent). `null` current tier locks every pip: the "not
 * started" state, with nothing earned and no pip marked current.
 */
const buildFlamePipStates = (pipCount: number, currentTier: BadgeLadderTier | null): FlamePipState[] =>
  Array.from({ length: pipCount }, (_unused, position) => {
    if (!currentTier) return "locked";
    if (position < currentTier.index) return "earned";
    if (position === currentTier.index) return "current";
    return "locked";
  });

/**
 * Rebuilt Lifetime Flame block (lifetime-flame-rebuild plan, flame-empty-state follow-up): a heading,
 * a row pairing the crystal emblem with the current tier's name and its subtitle, and a discrete pip
 * track — one dot per flame-catalog tier — captioned with the current rung's own name/threshold on the
 * left (as two lines: name, then amount) and the ladder's own top threshold on the right.
 * `current_tier`/`next_tier`/`points_to_next` stay entirely server-decided; only the catalog-derived
 * pip COUNT and top threshold are computed client-side, never which tier someone is actually in.
 *
 * Not-started (`current_tier: null`): every pip renders locked, and the name slots (row + track label)
 * show `FLAME_ZERO_TIER_NAME` ("Flint") — never the ladder's first rung (Spark isn't earned until 100,
 * so naming it here would claim a tier not yet reached — the flame-zero-label fix) and never the
 * literal string "Not started". The subtitle still names the first rung's own threshold via its
 * "lights your first Flame" wording — that number is correct to show, only the tier NAME was wrong.
 * The track label's amount does NOT fall back to the first rung's threshold either; it reads `0`,
 * matching the not-started frame's own "0 ⚡".
 *
 * The emblem is the current rung's own badge image — the same icon the Badges screen shows for that
 * tier — or, not started, the dedicated Flint emblem (`flameEmblemArt.ts`), which needs no catalog at
 * all. The generic crystal emblem (`flame-crystal-emblem.png`) stays as the fallback for the CURRENT-tier
 * case only — no rung image resolves because the catalog hasn't loaded yet, or the rung is missing from
 * it — so the card never renders an empty image; the not-started case never reaches this fallback.
 */

/**
 * The tier name shown in the not-started state (`current_tier: null`) — the thing you strike before
 * you have a spark, so it sits below the ladder's first rung ("Spark", earned at 100) without
 * colliding with "Kindle" at rung 3. A single constant, not a lookup: unlike the five core-value
 * ladders, which each need their own zero wording keyed by title (`ladderFillPercent.ts`'s
 * `ZERO_SPARKS_LABEL_BY_VALUE_TITLE`), the flame ladder is one ladder, so it gets one label
 * (flame-zero-label fix).
 */
const FLAME_ZERO_TIER_NAME = "Flint";

const FlameProgress: FC<FlameProgressProps> = (props) => {
  const { flame, badgeDefinitions } = props;
  const { lifetime_total, current_tier, next_tier, points_to_next, prestige_stars } = flame;

  const ladderTiers = getFlameLadderTiers(badgeDefinitions);
  const firstTier = ladderTiers[0] ?? null;
  const topTier = ladderTiers[ladderTiers.length - 1] ?? null;

  const pipStates = buildFlamePipStates(ladderTiers.length, current_tier);

  // Not-started fallback: current_tier is null, so there is no "current" rung to name. Naming the
  // first rung here would claim a tier not yet reached (Spark isn't earned until 100) — see
  // FLAME_ZERO_TIER_NAME's own comment — so this reads that one constant, never a firstTier/catalog
  // lookup and never the literal string "Not started". Always non-empty, so both name slots below
  // render unconditionally. Shared by both the emblem row's name and the track label's name — both
  // name the same rung, just at different sizes.
  const tierName = current_tier?.name ?? FLAME_ZERO_TIER_NAME;

  // Not-started gets its own wording; the populated state keeps flameSubtitle's existing copy.
  const subtitle = current_tier
    ? flameSubtitle(lifetime_total, points_to_next, next_tier)
    : `${lifetime_total.toLocaleString("en-GB")} all time · ${(firstTier?.threshold ?? 0).toLocaleString(
        "en-GB",
      )} ⚡ lights your first Flame`;

  // The threshold of the rung actually stood on — 0 when standing on none. Deliberately NOT a
  // firstTier fallback (that was the old behaviour): the not-started frame's own amount reads a
  // literal "0 ⚡", not the first rung's threshold.
  const trackLabelAmount = current_tier?.threshold ?? 0;

  const emblemSrc = flameEmblemArt(current_tier, badgeDefinitions) ?? flameCrystalEmblem;

  return (
    <div className="flame-progress">
      <CardHead title="Your Lifetime Flame" className="flame-progress-head" />
      <div className="flame-progress-current">
        <img src={emblemSrc} alt="" className="flame-progress-emblem" />
        <div className="flame-progress-tier">
          <div className="flame-progress-tier-name">{tierName}</div>
          <div className="flame-progress-subtitle">{subtitle}</div>
        </div>
      </div>
      <div className="flame-progress-track" data-testid="flame-progress-track">
        {pipStates.map((state, index) => (
          <span
            key={index}
            data-testid={`flame-progress-pip-${index}`}
            className={classnames("flame-progress-pip", `flame-progress-pip-${state}`)}
          />
        ))}
      </div>
      {/* The frame writes both thresholds as Sparks amounts -- "Blaze", "1,000 ⚡", "25,000 ⚡" -- with a
          thousands separator. The ⚡ is the same literal glyph the Redemptions stat cards already ship
          (not a new icon asset), and `toLocaleString` is what stops a five-figure ladder top rendering
          as a bare "25000". Name and amount are separate elements/lines, not one combined string. */}
      <div className="flame-progress-track-labels">
        <div className="flame-progress-track-label-left">
          <div className="flame-progress-track-label-tier-name">{tierName}</div>
          <div className="flame-progress-track-label-amount">{trackLabelAmount.toLocaleString("en-GB")} ⚡</div>
        </div>
        <div className="flame-progress-track-label-right">
          {topTier !== null ? `${topTier.threshold.toLocaleString("en-GB")} ⚡` : null}
        </div>
      </div>
      {/* No star asset exists anywhere in the repo (verified, unchanged from before this rebuild) — a
          plain glyph repeated prestige_stars times. The wrapper only renders when there is at least
          one star, so its PRESENCE (not just its glyph count) discriminates a `> 0` vs `>= 0` swap. */}
      {prestige_stars > 0 ? (
        <div className="flame-progress-prestige" data-testid="flame-progress-prestige">
          {"★".repeat(prestige_stars)}
        </div>
      ) : null}
    </div>
  );
};

export default FlameProgress;
